import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { sceneShots } from "@heroncendre/core";
import {
  pickText,
  selectGuideTrack,
  trackDuration,
  useGuided,
  useTourState,
  useTourStoreApi,
} from "@heroncendre/player";
import { cibleSceneSuivante, cibleScenePrecedente, segmentA, segmentsDuChapitre, type Segment } from "./segments.js";
import {
  DUREE_ANNONCE_MS,
  FONDU_ATTENTE_MAX_MS,
  FONDU_CHAPITRE_MS,
  etatUiInitial,
  modeGuide,
  reduireUi,
  type ModeGuide,
} from "./modeGuide.js";
import { storeProgression, useProgression } from "./guideProgress.js";
import {
  choisirChapitre,
  fermerMenu,
  menuFerme,
  ouvrirMenu,
  type Declencheur,
  type EtatMenu,
  type Resultat,
} from "./menuChapitres.js";

/**
 * ============================================================================
 *  useGuide.ts — Le guide, vu par l'interface
 * ----------------------------------------------------------------------------
 *  Branche l'interface sur ce qui existe : le lecteur audio (`narration` du store
 *  et son contrôleur) et la caméra du viewer (la séquence de plans du chapitre,
 *  en mode guide ; la caméra libre, en mode main). La machine à états est celle du
 *  moteur ; ici on la lit (`modeGuide`) et on l'actionne.
 *
 *  ── Le modèle ───────────────────────────────────────────────────────────────
 *
 *  `Guide` / `GuideChapter` / `GuideStep` du design se lisent dans les données
 *  EXISTANTES, sans les changer : le parcours (`timelines/`) est le guide, une étape
 *  est un chapitre (un guide du moteur, avec sa piste audio unique et ses
 *  sous-titres inline), et les plans de sa séquence (`sceneShot`, avec leurs
 *  keyframes) sont ses scènes — voir `segments.ts`.
 *
 *  ── Les règles de la main ──────────────────────────────────────────────────
 *
 *   - la voix continue en mode main ;
 *   - aucun changement de scène automatique en mode main : à la fin de la scène
 *     explorée la voix s'arrête et le bouton devient « Continuer le guide »
 *     (`guided.handManual`, `handEndAt` du moteur) ;
 *   - « Reprendre le guide » ramène la caméra sur la trajectoire en 0,8 s.
 * ==========================================================================*/

export interface Chapitre {
  index: number;
  /** Identifiant de l'étape de la timeline : celui que la progression mémorise. */
  id: string;
  guideId: string | undefined;
  titre: string;
  duree: number;
  segments: Segment[];
  sceneIds: readonly string[];
}

/**
 * Ce que la main manuelle ajoute au moteur (>= 0.18). Lu sans présumer qu'il est là :
 * sur un moteur plus ancien l'interface reste utilisable — la caméra du visiteur est
 * alors reprise seule et la séquence continue, comme avant — au lieu de s'effondrer
 * sur « setHandManual is not a function » (écran noir).
 */
interface MainManuelle {
  setHandManual?: (manuelle: boolean) => void;
}
const mainManuelle = (store: { getState(): unknown }) => store.getState() as MainManuelle;
const finDeSceneDe = (guided: unknown): boolean => ((guided as { handEndAt?: number | null }).handEndAt ?? null) !== null;

/** Une écriture de la position de reprise toutes les 3 s de voix, à peu près. */
const NOTER_MS = 3000;

/** Où mène la sortie du guide : l'écran de choix de la visite, ou la visite libre. */
export type SortieGuide = "accueil" | "libre";

export interface OptionsGuide {
  /** Le guide dont on mémorise la progression (`mariage`) ; absent, rien n'est mémorisé (`?demo`). */
  guideId?: string;
  /**
   * Appelé quand le visiteur quitte le guide, une fois le fondu fini : l'hôte repasse à l'accueil
   * (« Quitter le guide ») ou en visite libre (« Revenir à la visite libre »).
   */
  surSortie?: (vers: SortieGuide) => void;
}

export function useGuide({ guideId, surSortie }: OptionsGuide = {}) {
  const store = useTourStoreApi();
  const { pilot, timeline, controller, subtitlesOn, setSubtitlesOn } = useGuided();
  const tour = useTourState((s) => s.tour);
  const language = useTourState((s) => s.context.language);
  const etat = useTourState((s) => s.guided.state);
  const indexChapitre = useTourState((s) => s.guided.stepIndex);
  const finDeScene = useTourState((s) => finDeSceneDe(s.guided));
  const temps = useTourState((s) => s.narration.time);
  const duree = useTourState((s) => s.narration.duration);
  const lecture = useTourState((s) => s.narration.playing);
  const muet = useTourState((s) => s.narration.muted || s.narration.volume === 0);
  const sceneId = useTourState((s) => s.currentSceneId);
  const apparitions = useTourState((s) => s.sceneReveals);
  const [ui, envoyer] = useReducer(reduireUi, etatUiInitial);
  /** Fondu entre chapitres : le compteur d'apparitions à l'instant du changement, qu'il faut dépasser pour rendre l'image. */
  const attenteFondu = useRef<number | null>(null);
  const minuteurFondu = useRef(0);
  useEffect(() => () => window.clearTimeout(minuteurFondu.current), []);

  // La main reste au visiteur jusqu'à ce qu'il la rende : réglage de l'interface, pas du document.
  useEffect(() => {
    mainManuelle(store).setHandManual?.(true);
    return () => mainManuelle(store).setHandManual?.(false);
  }, [store]);

  const chapitres = useMemo<Chapitre[]>(
    () =>
      Array.from({ length: pilot.length }, (_, index) => {
        const guideId = pilot.guideIdAt(index);
        const choisi = selectGuideTrack(tour, guideId, language);
        const dureePiste = choisi ? (trackDuration(choisi.track) ?? 0) : 0;
        const etape = timeline.steps[index];
        return {
          index,
          id: etape?.id ?? `etape-${index}`,
          guideId,
          // Le titre du chapitre (`guides[].title`, réglé dans l'éditeur, onglet Voix) prime ;
          // le libellé de l'étape de la timeline n'est qu'un repli.
          titre: pickText(choisi?.guide.title, language) ?? (etape && pickText(etape.label, language)) ?? "",
          duree: dureePiste,
          segments: segmentsDuChapitre(sceneShots(choisi?.guide.cues ?? []), dureePiste),
          sceneIds: choisi?.guide.sceneIds ?? etape?.sceneIds ?? [],
        };
      }),
    [pilot, timeline, tour, language],
  );
  const chapitre = chapitres[indexChapitre];
  const segments = chapitre?.segments ?? [];
  const indexScene = segmentA(segments, temps);
  const scene = segments[indexScene];
  const nomScene = (id: string | undefined): string =>
    (id && pickText(tour.scenes.find((s) => s.id === id)?.title, language)) || "";

  const mode: ModeGuide | null = modeGuide(etat, ui.exploration);

  /* --- progression : chapitres vus, point de reprise ------------------------- */

  const progression = useProgression(guideId);
  const courantRef = useRef({ id: "", indexScene: 0, temps: 0, duree: 0, mode: null as ModeGuide | null });
  courantRef.current = { id: chapitre?.id ?? "", indexScene, temps, duree: chapitre?.duree ?? 0, mode };

  // Un chapitre est « vu » quand son écran de fin est atteint.
  useEffect(() => {
    if (!guideId || mode !== "fin" || !chapitre) return;
    const store = storeProgression(guideId);
    store.marquerVu(chapitre.id);
    store.noter({ chapterId: chapitre.id, sceneIndex: Math.max(0, chapitre.segments.length - 1), t: chapitre.duree });
    store.vider();
  }, [guideId, mode, chapitre]);

  // Où reprendre : la position de la voix, notée régulièrement tant qu'elle avance, et tout de
  // suite quand la page se masque ou se ferme (un onglet fermé n'attend pas le prochain tic).
  useEffect(() => {
    if (!guideId) return;
    const store = storeProgression(guideId);
    const noter = () => {
      const c = courantRef.current;
      if (!c.id || (c.mode !== "guide" && c.mode !== "main")) return;
      store.noter({ chapterId: c.id, sceneIndex: c.indexScene, t: Math.round(c.temps * 10) / 10 });
    };
    const minuteur = window.setInterval(noter, NOTER_MS);
    const masquer = () => {
      noter();
      store.vider();
    };
    const surVisibilite = () => {
      if (document.visibilityState === "hidden") masquer();
    };
    window.addEventListener("pagehide", masquer);
    document.addEventListener("visibilitychange", surVisibilite);
    return () => {
      window.clearInterval(minuteur);
      window.removeEventListener("pagehide", masquer);
      document.removeEventListener("visibilitychange", surVisibilite);
      masquer();
    };
  }, [guideId]);

  /* --- menu des chapitres ------------------------------------------------- */

  const [menu, setMenu] = useState<EtatMenu>(menuFerme);
  const menuRef = useRef(menu);
  menuRef.current = menu;
  /** Le bouton qui a ouvert le menu : le focus lui revient à la fermeture. */
  const declencheur = useRef<HTMLElement | null>(null);

  /* --- annonce de la main : 3,5 s ---------------------------------------- */

  const modePrecedent = useRef<ModeGuide | null>(null);
  useEffect(() => {
    if (mode === "main" && modePrecedent.current !== "main") envoyer({ type: "mainPrise" });
    modePrecedent.current = mode;
  }, [mode]);
  useEffect(() => {
    if (ui.annonce === null) return;
    const minuteur = window.setTimeout(() => envoyer({ type: "annonceExpiree" }), DUREE_ANNONCE_MS);
    return () => window.clearTimeout(minuteur);
  }, [ui.annonce]);

  // Un autre chapitre : plus d'exploration, plus de message du précédent.
  useEffect(() => envoyer({ type: "chapitreChange" }), [indexChapitre]);

  // Le noir se lève quand le rendu montre la scène du nouveau chapitre (`sceneReveals`) — pas
  // avant : sinon on verrait l'ancienne scène, encore à l'écran, s'effacer. Même règle que le
  // changement de scène de la navigation (`useChangementScene`).
  useEffect(() => {
    if (!ui.fondu || attenteFondu.current === null || apparitions <= attenteFondu.current) return;
    attenteFondu.current = null;
    window.clearTimeout(minuteurFondu.current);
    envoyer({ type: "fonduFin" });
  }, [ui.fondu, apparitions]);

  /* --- actions ------------------------------------------------------------ */

  const actions = useMemo(() => {
    const s = () => store.getState();

    /** Fondu au noir, changement de chapitre dans le noir, puis retour de l'image à l'apparition de sa scène. */
    const changerDeChapitre = (index: number) => {
      if (attenteFondu.current !== null || index === s().guided.stepIndex) return;
      if (index < 0 || index >= pilot.length) return;
      envoyer({ type: "fonduDebut" });
      // Verrou immédiat (un second clic pendant l'assombrissement ne relance rien) : jamais dépassé.
      attenteFondu.current = Number.POSITIVE_INFINITY;
      window.setTimeout(() => {
        const cible = pilot.stepAt(index)?.sceneIds[0];
        // Même scène en tête du chapitre suivant : rien ne « s'apparaît », on ne l'attend pas.
        const memeScene = cible === s().currentSceneId;
        attenteFondu.current = s().sceneReveals;
        const ecart = index - s().guided.stepIndex;
        // De proche en proche, les actions du moteur ; plus loin, on relance le parcours au chapitre voulu,
        // depuis sa scène 1, en mode guide.
        if (ecart === 1) s().nextStep();
        else if (ecart === -1) s().previousStep();
        else s().startGuided(pilot, index);
        window.clearTimeout(minuteurFondu.current);
        minuteurFondu.current = window.setTimeout(
          () => {
            attenteFondu.current = null;
            envoyer({ type: "fonduFin" });
          },
          memeScene ? 400 : FONDU_ATTENTE_MAX_MS,
        );
      }, FONDU_CHAPITRE_MS);
    };

    /** Applique ce que la logique du menu décide : pause, reprise de la voix, changement de chapitre. */
    const appliquerMenu = (resultat: Resultat) => {
      menuRef.current = resultat.etat;
      setMenu(resultat.etat);
      const effet = resultat.effet;
      if (effet === "pause") s().setNarrationPlaying(false);
      else if (effet === "lecture") s().setNarrationPlaying(true);
      else if (effet) changerDeChapitre(effet.chapitre);
    };

    return {
      /** Lecture / pause. En fin de scène explorée, relancer = continuer le guide. */
      basculerLecture: () => {
        if (finDeSceneDe(s().guided)) return s().returnToGuide();
        s().setNarrationPlaying(!s().narration.playing);
      },
      /** Aller à un instant du chapitre : la main revient au guide (le moteur le fait au saut). */
      aller: (secondes: number) => s().seekNarration(secondes),
      scenePrecedente: () => s().seekNarration(cibleScenePrecedente(segments, s().narration.time)),
      sceneSuivante: () => {
        const cible = cibleSceneSuivante(segments, s().narration.time);
        // À la dernière scène, ⏭ est la fin du chapitre.
        if (cible === null) return s().seekNarration(s().narration.duration);
        s().seekNarration(cible);
      },
      /** Rendre la main au guide relance la voix, même mise en pause pendant l'exploration. */
      reprendre: () => {
        s().setNarrationPlaying(true);
        s().returnToGuide();
      },
      /** La caméra est au visiteur : le moteur passe en main au premier geste ; ici, aux flèches. */
      tourner: (degres: number) => {
        s().takeControl();
        s().setView({ yaw: s().view.yaw + degres });
      },
      chapitrePrecedent: () => changerDeChapitre(s().guided.stepIndex - 1),
      chapitreSuivant: () => changerDeChapitre(s().guided.stepIndex + 1),
      /** Un chapitre précis, depuis sa scène 1 : même fondu au noir. */
      allerAuChapitre: changerDeChapitre,
      /** Le menu : la voix s'arrête à l'ouverture, et ne repart que si elle jouait. */
      ouvrirMenu: (origine: Declencheur, bouton?: HTMLElement | null) => {
        declencheur.current = bouton ?? null;
        const etat = s().guided.state;
        const jouait = s().narration.playing && (etat === "narration" || etat === "free");
        appliquerMenu(ouvrirMenu(menuRef.current, origine, { jouait, sceneFinie: finDeSceneDe(s().guided) }));
      },
      fermerMenu: () => appliquerMenu(fermerMenu(menuRef.current)),
      choisirChapitre: (index: number) => appliquerMenu(choisirChapitre(menuRef.current, index, s().guided.stepIndex)),
      basculerSon: () => s().setNarrationMuted(!s().narration.muted || s().narration.volume === 0),
      /** Une scène du chapitre, explorée sans voix (« main » sans narration). */
      explorer: (id: string) => {
        s().goToScene(id);
        envoyer({ type: "explorer" });
      },
      retourChapitre: () => {
        envoyer({ type: "retourChapitre" });
        // Revenir sur la scène d'entrée du chapitre : l'écran de fin est celui du chapitre, pas d'une scène.
      },
      /** Le chapitre reprend depuis son début : la voix et la séquence repartent de 0. */
      revoirChapitre: () => s().seekNarration(0),
      /**
       * Fondu vers le noir, voix arrêtée, puis l'hôte reprend la main : l'accueil par défaut
       * (« Quitter le guide » ramène au choix du type de visite), ou la visite libre sur la scène courante.
       */
      quitter: (vers: SortieGuide = "accueil") => {
        envoyer({ type: "quitter" });
        window.setTimeout(() => {
          controller.audio().pause();
          s().exitGuided();
          // Le lecteur tient encore la timeline : tant qu'elle y est, il croit le parcours en
          // cours et ne monte aucun hotspot. L'hôte repasse en visite libre (sans timeline).
          surSortie?.(vers);
        }, SORTIE_MS);
      },
    };
  }, [store, controller, segments, surSortie, pilot]);

  const sceneSuivanteExiste = cibleSceneSuivante(segments, temps) !== null;

  return {
    mode,
    ui,
    menu,
    declencheur,
    progression,
    actions,
    chapitres,
    chapitre,
    indexChapitre,
    premierChapitre: indexChapitre === 0,
    dernierChapitre: indexChapitre >= chapitres.length - 1,
    segments,
    indexScene,
    scene,
    nomScene,
    temps,
    duree,
    lecture: lecture && !finDeScene,
    finDeScene,
    muet,
    sceneId,
    sceneSuivanteExiste,
    soustitres: subtitlesOn,
    basculerSoustitres: () => setSubtitlesOn(!subtitlesOn),
    route: pilot.route,
  };
}

/** Durée du fondu vers le noir à la sortie (le design : 0,5 s). */
export const SORTIE_MS = 500;

export type Guide = ReturnType<typeof useGuide>;
